import React, { useState } from "react";
import styles from "./Payment.module.scss";
import { TipIcon } from "../../assets/icons/TipIcon";
import type { OrderPrice } from "@/shared/api/types";
import { LinkIcon } from "../../assets/icons";
import { PaymentMethod } from "./PaymentMethod";
import { usePaymentMethods } from "./usePaymentMethods";
import { Card } from "@/shared/ui";
import { Link } from "@tanstack/react-router";
import { LEGAL_DOCUMENTS } from "@/modules/Legal/model/legal-documents";

const OFFER_URL = LEGAL_DOCUMENTS.offer.url;
const SUPPORT_URL = "https://t.me/selleralarm_support_bot";

interface PaymentProps {
  email: string;
  setEmail: (email: string) => void;
  emailError: string | null;
  setEmailError: (error: string | null) => void;
  promocode: string;
  setPromocode: (promocode: string) => void;
  promoError: string | null;
  promoSuccess: string | null;
  orderPrice: OrderPrice | null;
  isProcessing: boolean;
  handleApplyPromocode: () => void;
  handleProceedToPayment: (paymentId: string | null) => void;
  handleNavigateBack: () => void;
}

export const Payment: React.FC<PaymentProps> = ({
  email,
  setEmail,
  emailError,
  setEmailError,
  promocode,
  setPromocode,
  promoError,
  promoSuccess,
  orderPrice,
  isProcessing,
  handleApplyPromocode,
  handleProceedToPayment,
  handleNavigateBack,
}) => {
  const [isAgreed, setIsAgreed] = useState(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);

  const { savedMethods } = usePaymentMethods();

  const promoDiscount = orderPrice?.discounts.find(
    (d) => d.reason === "PROMOCODE",
  );

  const handlePaymentMethodClick = (method: string) => {
    setPaymentId(method);
  };

  const isSavedMethodSelected = paymentId !== null;

  return (
    <Card variant="narrow">
      <div className={styles.paymentContent}>
        <h2 className={styles.sectionTitle}>Детали оплаты</h2>

        <div className={styles.paymentInfo}>
          {savedMethods.length > 0 && (
            <div>
              <h2 className={styles.sectionTitle}>Сохранённые способы</h2>
              <ul className={styles.paymentMethods}>
                {savedMethods.map((method) => (
                  <PaymentMethod
                    key={method.method}
                    method={method}
                    onClick={handlePaymentMethodClick}
                    isSelected={method.method === paymentId}
                  />
                ))}
                <li
                  className={`${styles.paymentMethod} ${paymentId === null ? styles.selectedMethod : ""}`}
                  onClick={() => setPaymentId(null)}
                >
                  <div className={styles.paymentName}>Новый способ оплаты</div>
                </li>
              </ul>
            </div>
          )}
          <div
            className={`${styles.paymentInputGroup} ${
              emailError ? styles.promoError : ""
            }`}
          >
            <div className={styles.paymentInputLabel}>
              {emailError || "E-mail для чека"}
            </div>
            <input
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) {
                  setEmailError(null);
                }
              }}
              className={`${styles.paymentInput} ${styles.labeledPaymentInput}`}
              type="email"
              placeholder="user@example.com"
            />
          </div>
          <div
            className={`${styles.paymentInputGroup} ${
              promoError ? styles.promoError : ""
            }`}
          >
            <div className={styles.paymentInputLabel}>
              {promoError || promoSuccess || "Промокод"}
            </div>
            {promoDiscount && (
              <div className={styles.paymentInputPercent}>
                -{promoDiscount.discount}%
              </div>
            )}
            <input
              value={promocode}
              onChange={(e) => setPromocode(e.target.value.toUpperCase())}
              className={`${styles.paymentInput} ${styles.labeledPaymentInput}`}
              type="text"
            />
            <button
              className={styles.promoSubmit}
              onClick={handleApplyPromocode}
            >
              <TipIcon />
            </button>
          </div>
        </div>

        <div className={styles.sum}>
          <span className={styles.sumLabel}>Итоговая сумма:</span>
          {orderPrice ? (
            <div className={styles.priceBlock}>
              {orderPrice.price < orderPrice.price_without_discount && (
                <span className={styles.oldPrice}>
                  {orderPrice.price_without_discount} ₽
                </span>
              )}
              <span className={styles.price}>{orderPrice.price} ₽</span>
            </div>
          ) : (
            <div className={styles.spinner}></div>
          )}
        </div>

        <a
          className={styles.offerLink}
          href={OFFER_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div>Публичная оферта</div>
          <LinkIcon />
        </a>

        <Link to="/legal" className={styles.offerLink}>
          <div>Правовые документы</div>
        </Link>

        <div className={styles.offer}>
          <input
            type="checkbox"
            id="offer-agreement"
            checked={isAgreed}
            onChange={(e) => setIsAgreed(e.target.checked)}
          />
          <label htmlFor="offer-agreement">Я согласен с условиями оферты</label>
        </div>

        <div className={styles.paymentButttons}>
          <button
            disabled={!isAgreed || isProcessing}
            className={styles.primaryButton}
            onClick={() => handleProceedToPayment(paymentId)}
          >
            {isProcessing ? (
              <div className={styles.spinner}></div>
            ) : isSavedMethodSelected ? (
              "Оплатить"
            ) : (
              "Перейти к оплате"
            )}
          </button>
          <button
            className={styles.secondaryButton}
            onClick={handleNavigateBack}
          >
            Изменить ссылку таблицы
          </button>
          <button
            className={styles.secondaryButton}
            onClick={(e) => {
              e.preventDefault();
              window.open(SUPPORT_URL, "_blank");
            }}
          >
            Поддержка
          </button>
        </div>
      </div>
    </Card>
  );
};
